import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Calendar,
  Download,
  RefreshCw
} from 'lucide-react';
import { AdminBetsAPI } from '../services/api';

const Bets = () => {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    per_page: 20
  });
  const [filters, setFilters] = useState({
    status: '',
    bet_type: '',
    search: '',
    date_from: '',
    date_to: '',
    min_amount: '',
    max_amount: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedBet, setSelectedBet] = useState(null);
  const [showBetModal, setShowBetModal] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Fetch bets
  const fetchBets = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        limit: pagination.per_page,
        ...filters
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key] || params[key] === '') {
          delete params[key];
        }
      });
      
      const response = await AdminBetsAPI.getBets(params);
      
      if (response.success) {
        setBets(response.data || []);
        setPagination(response.pagination || {
          current_page: 1,
          total_pages: 1,
          total_records: 0,
          per_page: 20
        });
      } else {
        setError(response.error || 'Failed to fetch bets');
      }
    } catch (err) {
      // console.error('Failed to fetch bets:', err);
      setError('Failed to load bets. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.per_page]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await AdminBetsAPI.getBetStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      // console.error('Failed to fetch stats:', err);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchBets(1);
    fetchStats();
  }, [fetchBets, fetchStats]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    fetchBets(1);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: '',
      bet_type: '',
      search: '',
      date_from: '',
      date_to: '',
      min_amount: '',
      max_amount: ''
    });
    setTimeout(() => fetchBets(1), 100);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchBets(newPage);
    }
  };

  // View bet details
  const handleViewBet = async (betId) => {
    try {
      const response = await AdminBetsAPI.getBet(betId);
      if (response.success) {
        setSelectedBet(response.data);
        setShowBetModal(true);
      }
    } catch (err) {
      // console.error('Failed to fetch bet details:', err);
    }
  };

  // Update bet status
  const handleUpdateStatus = async (betId, status) => {
    if (!window.confirm(`Are you sure you want to change this bet to "${status}"?`)) {
      return;
    }
    
    try {
      setUpdating(true);
      const response = await AdminBetsAPI.updateBetStatus(betId, status);
      
      if (response.success) {
        // Refresh data
        fetchBets(pagination.current_page);
        fetchStats();
        if (showBetModal) {
          setShowBetModal(false);
        }
      } else {
        alert(response.error || 'Failed to update bet status');
      }
    } catch (err) {
      // console.error('Failed to update bet status:', err);
      alert('Failed to update bet status');
    } finally {
      setUpdating(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      'won': 'bg-green-500/10 text-green-500 border-green-500/20',
      'lost': 'bg-red-500/10 text-red-500 border-red-500/20',
      'cancelled': 'bg-gray-500/10 text-gray-400 border-gray-500/20'
    };
    
    const icons = {
      'pending': <Clock className="w-3 h-3" />,
      'won': <CheckCircle className="w-3 h-3" />,
      'lost': <XCircle className="w-3 h-3" />,
      'cancelled': <AlertCircle className="w-3 h-3" />
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Bets Management</h1>
            <p className="text-charcoal-gray text-sm mt-1">View and manage all user bets</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { fetchBets(pagination.current_page); fetchStats(); }}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg transition-colors text-sm font-medium"
            >
              Apply Filters
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-charcoal-gray">Total Bets</p>
                  <p className="text-2xl font-bold text-white">{stats.overview.total_bets || 0}</p>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-lg">
                  <DollarSign className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </div>
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-charcoal-gray">Pending Bets</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.overview.pending_bets || 0}</p>
                </div>
                <div className="p-3 bg-yellow-500/10 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-400" />
                </div>
              </div>
            </div>
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-charcoal-gray">Won Bets</p>
                  <p className="text-2xl font-bold text-green-400">{stats.overview.won_bets || 0}</p>
                </div>
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </div>
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-charcoal-gray">Total Staked</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(stats.overview.total_staked || 0)}</p>
                </div>
                <div className="p-3 bg-purple-500/10 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-charcoal-gray mb-1">Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by user or selection..."
                className="w-full bg-gray-800 text-white rounded-lg border border-gray-700 px-3 py-2 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              />
            </div>
            <div>
              <label className="block text-sm text-charcoal-gray mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full bg-gray-800 text-white rounded-lg border border-gray-700 px-3 py-2 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-charcoal-gray mb-1">Bet Type</label>
              <select
                value={filters.bet_type}
                onChange={(e) => handleFilterChange('bet_type', e.target.value)}
                className="w-full bg-gray-800 text-white rounded-lg border border-gray-700 px-3 py-2 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              >
                <option value="">All Types</option>
                <option value="match_winner">Match Winner</option>
                <option value="over_under">Over/Under</option>
                <option value="both_teams_score">Both Teams Score</option>
                <option value="draw_no_bet">Draw No Bet</option>
                <option value="double_chance">Double Chance</option>
                <option value="accumulator">Accumulator</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-charcoal-gray mb-1">Date From</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full bg-gray-800 text-white rounded-lg border border-gray-700 px-3 py-2 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              />
            </div>
            <div>
              <label className="block text-sm text-charcoal-gray mb-1">Date To</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full bg-gray-800 text-white rounded-lg border border-gray-700 px-3 py-2 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              />
            </div>
            <div>
              <label className="block text-sm text-charcoal-gray mb-1">Min Amount</label>
              <input
                type="number"
                value={filters.min_amount}
                onChange={(e) => handleFilterChange('min_amount', e.target.value)}
                placeholder="0.00"
                className="w-full bg-gray-800 text-white rounded-lg border border-gray-700 px-3 py-2 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              />
            </div>
            <div>
              <label className="block text-sm text-charcoal-gray mb-1">Max Amount</label>
              <input
                type="number"
                value={filters.max_amount}
                onChange={(e) => handleFilterChange('max_amount', e.target.value)}
                placeholder="0.00"
                className="w-full bg-gray-800 text-white rounded-lg border border-gray-700 px-3 py-2 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={resetFilters}
                className="w-full px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors text-sm font-medium"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Bets Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-gray-700 border-t-sky-500 rounded-full animate-spin mx-auto"></div>
              <p className="text-charcoal-gray mt-4">Loading bets...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-400">{error}</p>
            <button
              onClick={() => fetchBets(1)}
              className="mt-3 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors text-sm"
            >
              Try Again
            </button>
          </div>
        ) : bets.length === 0 ? (
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-12 text-center">
            <DollarSign className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No bets found</h3>
            <p className="text-charcoal-gray">No bets match your filters.</p>
          </div>
        ) : (
          <>
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800/50 border-b border-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-charcoal-gray uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-charcoal-gray uppercase tracking-wider">Selection</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-charcoal-gray uppercase tracking-wider">Stake</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-charcoal-gray uppercase tracking-wider">Odds</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-charcoal-gray uppercase tracking-wider">Potential Win</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-charcoal-gray uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-charcoal-gray uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-charcoal-gray uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {bets.map((bet) => (
                      <tr key={bet.id} className="hover:bg-gray-800/30 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm text-white font-medium">{bet.username || 'N/A'}</p>
                            <p className="text-xs text-charcoal-gray">{bet.email || ''}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm text-white">{bet.selection_name || bet.bet_type}</p>
                            <p className="text-xs text-charcoal-gray">
                              {bet.home_team} vs {bet.away_team}
                            </p>
                            {bet.is_accumulator && (
                              <span className="text-xs text-purple-400">⚡ Accumulator</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-white font-medium">{formatCurrency(bet.stake)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-white">{parseFloat(bet.odds).toFixed(2)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-green-400 font-medium">{formatCurrency(bet.potential_win)}</span>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(bet.status)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-charcoal-gray">
                            {formatDate(bet.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleViewBet(bet.id)}
                              className="p-1.5 text-gray-400 hover:text-sky-400 hover:bg-sky-500/10 rounded transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {bet.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleUpdateStatus(bet.id, 'won')}
                                  className="p-1.5 text-gray-400 hover:text-green-400 hover:bg-green-500/10 rounded transition-colors"
                                  title="Mark as Won"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleUpdateStatus(bet.id, 'lost')}
                                  className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                  title="Mark as Lost"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-charcoal-gray">
                  Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total_records)} of{' '}
                  {pagination.total_records} bets
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                      let pageNum;
                      if (pagination.total_pages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.current_page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.current_page >= pagination.total_pages - 2) {
                        pageNum = pagination.total_pages - 4 + i;
                      } else {
                        pageNum = pagination.current_page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                            pageNum === pagination.current_page
                              ? 'bg-sky-500 text-white'
                              : 'text-gray-400 hover:text-white hover:bg-gray-800'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    {pagination.total_pages > 5 && pagination.current_page < pagination.total_pages - 2 && (
                      <>
                        <span className="text-charcoal-gray">...</span>
                        <button
                          onClick={() => handlePageChange(pagination.total_pages)}
                          className="w-9 h-9 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
                        >
                          {pagination.total_pages}
                        </button>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.total_pages}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Bet Details Modal */}
        {showBetModal && selectedBet && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="bg-gray-900 rounded-2xl max-w-2xl w-full p-6 relative border border-gray-800 max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => setShowBetModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
              
              <h2 className="text-xl font-bold text-white mb-4">Bet Details</h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-charcoal-gray">User</p>
                    <p className="text-white font-medium">{selectedBet.username || 'N/A'}</p>
                    <p className="text-sm text-charcoal-gray">{selectedBet.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-charcoal-gray">Status</p>
                    {getStatusBadge(selectedBet.status)}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-charcoal-gray">Stake</p>
                    <p className="text-white font-medium">{formatCurrency(selectedBet.stake)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-charcoal-gray">Odds</p>
                    <p className="text-white font-medium">{parseFloat(selectedBet.odds).toFixed(2)}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-charcoal-gray">Potential Win</p>
                    <p className="text-green-400 font-medium">{formatCurrency(selectedBet.potential_win)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-charcoal-gray">Date</p>
                    <p className="text-white">{formatDate(selectedBet.created_at)}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-charcoal-gray">Selection</p>
                  <p className="text-white">{selectedBet.selection_name || selectedBet.bet_type}</p>
                  {selectedBet.fixture_id && (
                    <p className="text-sm text-charcoal-gray">
                      {selectedBet.home_team} vs {selectedBet.away_team}
                    </p>
                  )}
                </div>
                
                {selectedBet.is_accumulator && selectedBet.selections && (
                  <div>
                    <p className="text-sm text-charcoal-gray mb-2">Accumulator Selections</p>
                    <div className="bg-gray-800 rounded-lg p-3 space-y-2">
                      {typeof selectedBet.selections === 'string' 
                        ? JSON.parse(selectedBet.selections).map((sel, idx) => (
                            <div key={idx} className="text-sm text-white">
                              {sel.selection_name || sel.bet_type} (Odds: {sel.odds})
                            </div>
                          ))
                        : selectedBet.selections.map((sel, idx) => (
                            <div key={idx} className="text-sm text-white">
                              {sel.selection_name || sel.bet_type} (Odds: {sel.odds})
                            </div>
                          ))
                      }
                    </div>
                  </div>
                )}
                
                {selectedBet.history && selectedBet.history.length > 0 && (
                  <div>
                    <p className="text-sm text-charcoal-gray mb-2">History</p>
                    <div className="bg-gray-800 rounded-lg p-3 space-y-1 max-h-32 overflow-y-auto">
                      {selectedBet.history.map((h, idx) => (
                        <div key={idx} className="text-xs text-charcoal-gray">
                          {h.action}: {h.previous_status} → {h.new_status} ({formatDate(h.created_at)})
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedBet.status === 'pending' && (
                  <div className="flex gap-3 pt-4 border-t border-gray-800">
                    <button
                      onClick={() => handleUpdateStatus(selectedBet.id, 'won')}
                      disabled={updating}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold transition-colors disabled:opacity-50"
                    >
                      Mark as Won
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(selectedBet.id, 'lost')}
                      disabled={updating}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-semibold transition-colors disabled:opacity-50"
                    >
                      Mark as Lost
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bets;